/**
 * `payment_method` 两层取值的**静态形态**部分（admin 全仓单点）
 *
 * 契约源：dev-docs `03-数据字典/70-产品配置.md` §3「`payment_method` 两层闭集」+ §3.0（全局支付方式目录）
 * + §3.7.5（Stripe 同步子集）。同一个列名 `payment_method` 承载两个不同层的取值域，按**承载位置**分层，不可互换：
 *
 * | 层 | 取值域 | admin 侧承载位置 |
 * |---|---|---|
 * | 支付方式 | **按 App 求值** = `system_payment_methods` 中 ACTIVE 且平台适用的目录行 ∪ 该 App 的 Stripe 同步子集 | 策略成员（§3.7.2）· 图标设置（§3.7.3）· A8 §7.8.1~§7.8.10 |
 * | 结算通道 | `SETTLEMENT_CHANNELS` = `IAP` / `STRIPE` ∪ 已登记框架通道（`FRAMEWORK_SETTLEMENT_CHANNELS`） | `app_payment_methods` · `product_metadata` · `app_stripe_checkout_configs` · 购买记录 / 财务 / 看板渠道维度 |
 *
 * **固定子集、`channel_of`、适用平台、后台展示名与分组一律读目录**（§3 反模式：不在 admin 另维护固定清单、映射或展示名）：
 * server 侧经 `packages/server/src/lib/payment-method-catalog.ts` 请求内现读，web 侧取 A8 §7.8.8 可选列表的
 * `channel` / `label` / `group` 字段。本文件只保留与目录无关的形态与结算通道闭集。
 */

import { z } from 'zod';

/**
 * Stripe 同步取值的形态：`STRIPE_` + Stripe 类型键（`^[a-z][a-z0-9_]*$`）转大写（§3.7.5），
 * 与 C 端 `STRIPE_SYNCED_PAYMENT_METHOD_PATTERN` 逐字一致。形态只是必要条件：与目录行同名的值不是同步取值，
 * 某值是否属于某 App 的同步子集须按该 App 的同步行判定（server `payment-method-catalog.ts`）。
 */
export const STRIPE_SYNCED_PAYMENT_METHOD_PATTERN = /^STRIPE_[A-Z][A-Z0-9_]*$/;

/**
 * 支付方式取值的最宽形态 = 目录行 `payment_method` 的格式（70 §3.0：`^[A-Z][A-Z0-9_]{0,31}$`，
 * 即策略成员 / 图标表 `varchar(32)`）。同步取值（`STRIPE_` + ≤25 位类型键）同样落在此形态内。
 */
export const PAYMENT_METHOD_VALUE_PATTERN = /^[A-Z][A-Z0-9_]{0,31}$/;

/**
 * 支付方式层取值（形态级）。**类型上是 string** —— 固定子集由目录行承载、同步子集按 App 动态求值，
 * 编译期都无法穷举。
 */
export type PaymentMethod = string;

/**
 * 该取值是否具备支付方式层的形态（入参 Zod 与 DB 读出值的窄化守卫；App 归属另由 service 判）。
 * 结算通道值 `STRIPE` 恒不是支付方式（目录 CHECK `stripe_requires_method_key`：STRIPE 行必有方式键）。
 */
export function isPaymentMethodShape(value: string): boolean {
  return value !== 'STRIPE' && PAYMENT_METHOD_VALUE_PATTERN.test(value);
}

/**
 * 支付方式入参 schema（形态级）。结算通道值 `STRIPE` 与小写 / 超长取值即拒；
 * 「属不属于目标 App 的可用值域」是跨表事实（目录行状态 / 平台 + 本 App 同步行），由 service 在 App 级守卫锁内判
 * （A8 §7.8「可选支付方式」）。
 */
export const PaymentMethodSchema = z
  .string()
  .refine(isPaymentMethodShape, { message: 'payment_method is not a payment method value' });

/**
 * 已登记框架通道（`02-API契约/65-三方支付通道框架.md` §2「已登记通道」；当前 `PAYERMAX`（§2.1）、`AIRWALLEX`（§2.2））。
 *
 * **admin 侧登记来源 = 本常量**（shared 被 web 共用、读不到库，故不在运行期读目录）。上游来源是 server
 * `src/lib/payment-providers/framework-settlement-channels.ts` 的 `REGISTERED_FRAMEWORK_SETTLEMENT_CHANNELS`（C 端
 * 框架通道 CHECK 与能力声明注册表的同一来源）；admin 跟进上游登记靠镜像 CHECK 的跟进（同 #1139 跟进 0262），
 * 不在本仓机读 server 常量（#1139 跟进 0262 登记 PAYERMAX，dd-issue1578 跟进 0268 登记 AIRWALLEX）。
 * `packages/server/src/lib/settlement-channel-registration.test.ts` 钉住 admin 内三处同值：
 * 本常量、镜像里的通道闭集 CHECK（`product_metadata_payment_method` / `app_payment_methods_active_payment_method_valid` /
 * Adapty store CHECK）、测试目录种子（`test-fixtures/payment-method-catalog-seed.ts`，逐行取自 C 端迁移）中
 * IAP / STRIPE 之外的 `channel`。只改其中一处，该测试即红。
 *
 * 登记一条新通道的顺序：契约 §2 先登记 → server 加常量与 migration → admin 镜像跟进 CHECK 与目录种子 → 本常量加一项。
 *
 * 当前登记的框架通道都含原生平台且登记了 Adapty 中继（PAYERMAX / AIRWALLEX：IOS · ANDROID · WEB，`SET_TRANSACTION`），与 C 端
 * 可售判定所用的「原生框架通道」子集（`registeredAdaptyStoreChannels`）同值；登记只含 WEB 的框架通道时，原生
 * `product_metadata` 的适用面须改按该子集判定，不能直接扩本常量。
 */
export const FRAMEWORK_SETTLEMENT_CHANNELS = ['PAYERMAX', 'AIRWALLEX'] as const;
export type FrameworkSettlementChannel = (typeof FRAMEWORK_SETTLEMENT_CHANNELS)[number];

/**
 * 结算通道层闭集（钱从哪条路走）= `IAP` · `STRIPE` ∪ 已登记框架通道（70 §3「两层闭集」）。
 * 新增固定支付方式或同步出新类型都不动本闭集（§3 判据）；只有新登记框架通道才扩它，且由上方常量派生。
 */
export const SETTLEMENT_CHANNELS = ['IAP', 'STRIPE', ...FRAMEWORK_SETTLEMENT_CHANNELS] as const;
export const SettlementChannelSchema = z.enum(SETTLEMENT_CHANNELS);
export type SettlementChannel = z.infer<typeof SettlementChannelSchema>;

/** 该取值是否属结算通道层（A2 §6.2 placement 入参判定，及 DB 读出值的窄化守卫）。 */
export function isSettlementChannel(value: string): value is SettlementChannel {
  return (SETTLEMENT_CHANNELS as readonly string[]).includes(value);
}

/** 该结算通道是否为已登记框架通道（不含 IAP / STRIPE，二者走各自专属实现，字典 65 文件头）。 */
export function isFrameworkSettlementChannel(value: string): value is FrameworkSettlementChannel {
  return (FRAMEWORK_SETTLEMENT_CHANNELS as readonly string[]).includes(value);
}

/**
 * 该结算通道的 `product_metadata` 行是否以 IAP 行为基线派生权益（STRIPE ∪ 已登记框架通道，70 §2：框架通道
 * 「权益派生与 `is_serveable` 复用 STRIPE 口径」）。这些行建档 / 编辑 / 上架都要命中同 access level 的 IAP 基线，
 * 也是畅读卡卡位里 IAP 行的配对行（70 §2.5「卡位参与通道」）。
 *
 * **不要用 `!== 'IAP'` 代替本判定**：闭集外的取值（软删历史行 / 裸 SQL）既不是 IAP 也不是配对通道。
 */
export function isIapPairedSettlementChannel(
  value: string,
): value is 'STRIPE' | FrameworkSettlementChannel {
  return value === 'STRIPE' || isFrameworkSettlementChannel(value);
}
